import { IClinicTotals, IncomeMode, IRevenueShareLabel, ITherapistBreakdown, shareOf } from "./models/clinicDashboard";
import { localised } from "./models/localisedText";
import { formatPrice } from "./purchaseDisplay";
import ResourcesIds from "../resources/ResourcesIds";

/**
 * How the dashboard writes its numbers, in one place -- the same reason purchaseDisplay
 * and membershipDisplay exist. A figure that reads one way on a tile and another way in
 * the table beside it is a bug rather than a style choice.
 */

/** The currency is inside the translated string, as everywhere else in this app. */
export const formatMoney = (amount: number, translate: (key: string, options?: object) => string): string =>
    translate(ResourcesIds.DASHBOARD_MONEY, { amount: formatPrice(amount) });

export const INCOME_MODE_LABELS: Record<IncomeMode, string> = {
    BILLED: ResourcesIds.DASHBOARD_INCOME_BILLED,
    EARNED: ResourcesIds.DASHBOARD_INCOME_EARNED,
};

export const INCOME_MODE_HELP: Record<IncomeMode, string> = {
    BILLED: ResourcesIds.DASHBOARD_INCOME_BILLED_HELP,
    EARNED: ResourcesIds.DASHBOARD_INCOME_EARNED_HELP,
};

/**
 * A therapist's name, or the label for the bucket of purchases that name nobody.
 *
 * The API sends null rather than a label there because it has no locale, so every place
 * that renders a therapist has to make this decision -- and has to make the same one.
 */
export const therapistLabel = (
    row: { therapistName: string | null },
    translate: (key: string) => string
): string => row.therapistName ?? translate(ResourcesIds.DASHBOARD_THERAPIST_UNASSIGNED);

/**
 * A revenue-share rate as the page says it out loud.
 *
 * The API sends a decimal because the column has scale for a clinic that one day splits 42.5;
 * the UI offers whole percents, so a stored 45.00 must not print as "45.00%". Trailing zeros
 * are dropped and a real fraction is kept.
 */
export const formatPercent = (percent: number): string =>
    String(Number(percent.toFixed(2)));

/**
 * What a therapist keeps of a row, or an em dash where the clinic has set them no share.
 *
 * The dash rather than a repeat of the income beside it: those two numbers are equal for a
 * therapist on no share, and a column of pairs of identical figures says nothing except that
 * the clinic has not configured anything. The dash says exactly that, and says it once.
 *
 * Follows the same mode as the income column it sits next to -- see `shareOf`. Here rather than
 * in the table for the reason `therapistLabel` is: the table and the compact card must not
 * describe the same row differently.
 */
export const therapistShare = (
    row: ITherapistBreakdown | IClinicTotals,
    mode: IncomeMode,
    translate: (key: string, options?: object) => string
): string => {
    const hasShare: boolean = 'revenueShares' in row && row.revenueShares.length > 0;

    return hasShare ? formatMoney(shareOf(row, mode), translate) : '—';
};

/**
 * A therapist's rates in words: "40% · Masaj prenatal 50%". The default reads as a bare percent
 * and each override names its discipline, in the reader's language where the clinic wrote one.
 *
 * Empty for no rates, which the caller renders as nothing at all rather than as "100%".
 */
export const describeRevenueShares = (
    labels: IRevenueShareLabel[],
    language: string,
    translate: (key: string, options?: object) => string
): string =>
    labels
        .map(label => label.catalogTypeId === null
            ? translate(ResourcesIds.REVENUE_SHARE_PERCENT_VALUE, {
                percent: formatPercent(label.percent),
            })
            : translate(ResourcesIds.THERAPIST_INCOME_SHARE_RATE, {
                name: localised(label.nameRo ?? '', label.nameEn, language),
                percent: formatPercent(label.percent),
            }))
        .join(' · ');

/**
 * A month key from the API ("2026-08") as a short month name in the reader's language.
 *
 * Parsed as a local date rather than through `new Date("2026-08")`, which reads a
 * bare year-month as UTC and can land the label a month early west of Greenwich.
 */
export const formatTrendMonth = (month: string, i18n: { language: string }): string => {
    const [year, monthOfYear] = month.split('-').map(Number);

    return new Date(year, monthOfYear - 1, 1).toLocaleDateString(i18n.language, { month: 'short' });
};

/**
 * Money as it fits above a bar: whole units, grouped, no currency.
 *
 * "12345.00 RON" is around 90px of text over a bar that may be 25px wide. The exact amount
 * is one hover away in the tooltip and spelled out in full in the table below; what the bar
 * label is for is the shape of the year at a glance.
 */
export const compactMoney = (amount: number, i18n: { language: string }): string =>
    Math.round(amount).toLocaleString(i18n.language, { maximumFractionDigits: 0 });

/**
 * The label printed above each bar.
 *
 * This has to be the function form rather than the chart's built-in `barLabel="value"`:
 * that shortcut returns null for 0 and the renderer then skips the label entirely, so a
 * month with no activity would print nothing where the reader expects a `0`. Reading a gap
 * as "nothing happened" rather than "we did not measure" is the whole reason those zeros
 * are on screen.
 *
 * `showZeros` exists so that if a hundred printed zeros turn out to be noise in practice,
 * it is one argument to change rather than a redesign.
 */
export const barValueLabel =
    (format: (value: number) => string, showZeros: boolean = true) =>
    (item: { value: number | null }): string | null => {
        const value = item.value ?? 0;

        return value === 0 && !showZeros ? null : format(value);
    };
